import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { doc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Buffer } from "buffer";
import { db, storage } from "../firebase";
import { useVenue } from "./VenueProvider";
import { showAlert } from "../alertService";
import { useAuth } from "./AuthProvider";
import { esSoloLectura } from "../utils/roles";

if (!window.Buffer) window.Buffer = Buffer;

const ACCENT = "#1b4d2e";

const PremiumTag = ({ show }) =>
  show ? (
    <span className="ml-1.5 inline-flex items-center rounded bg-amber-100 text-amber-700 px-1.5 py-0.5 text-[10px] font-semibold leading-none align-middle">
      PREMIUM
    </span>
  ) : null;

/** Default config used for free-tier PDF output. */
const FREE_DEFAULTS = {
  primaryColor: "#1b4d2e",
  accentColor: "#1b4d2e",
  secondaryColor: "#f0fdf4",
  logoUrl: "",
  logoSize: 60,
  headerHeight: 80,
  showVenueName: true,
  direccion: "",
  telefono: "",
  correo: "",
  vigencia: "30 días",
  leyendaExtra: "",
};

/** Given a hex color, return a very light tint (mix ~90% white). */
const deriveSecondaryColor = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const mix = 0.9;
  const lr = Math.round(r + (255 - r) * mix);
  const lg = Math.round(g + (255 - g) * mix);
  const lb = Math.round(b + (255 - b) * mix);
  return `#${lr.toString(16).padStart(2, "0")}${lg.toString(16).padStart(2, "0")}${lb.toString(16).padStart(2, "0")}`;
};

const SAMPLE_EVENT = {
  cliente: "Juan",
  apellido: "Pérez",
  nombreEmpresa: "Eventos MX",
  correo: "juan@email.com",
  telefono: "55 1234 5678",
  nombre: "Boda García - López",
  start: { seconds: Math.floor(Date.now() / 1000) + 86400 * 30 },
  end: { seconds: Math.floor(Date.now() / 1000) + 86400 * 30 + 3600 * 6 },
  numPeople: 150,
};

const SAMPLE_DESGLOSE = [
  { concept: "Renta del salón", units: 1, unitPrice: 45000 },
  { concept: "Montaje de mesas", units: 20, unitPrice: 350 },
  { concept: "Iluminación decorativa", units: 1, unitPrice: 8000 },
  { concept: "Servicio de meseros", units: 5, unitPrice: 1500 },
  { concept: "Descuento por temporada", units: 1, unitPrice: -5000 },
];

const CotizacionPreview = ({ config, isPremium, venueName, previewMode }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const configKey = JSON.stringify(config) + isPremium + venueName + previewMode;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const [{ pdf }, { default: CotizacionPDF }] = await Promise.all([
          import("@react-pdf/renderer"),
          import("./CotizacionPDF"),
        ]);
        const React = await import("react");
        const showAsPremium = previewMode === "premium";
        const docEl = React.createElement(CotizacionPDF, {
          venue: { name: venueName },
          event: SAMPLE_EVENT,
          desglose: SAMPLE_DESGLOSE,
          ivaEnabled: true,
          folio: "COT-0001",
          isPremium: showAsPremium,
          cotizacionConfig: config,
        });
        const blob = await pdf(docEl).toBlob();
        const url = URL.createObjectURL(blob);
        setPdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      } catch (err) {
        console.error("Preview generation failed:", err);
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="rounded-xl border border-zinc-200 bg-zinc-100 shadow-sm overflow-hidden relative"
      style={{ aspectRatio: "210 / 297" }}
    >
      {loading && !pdfUrl && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-zinc-400">
          Generando vista previa...
        </div>
      )}
      {loading && pdfUrl && (
        <div className="absolute top-2 right-2 z-10">
          <div className="h-4 w-4 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
        </div>
      )}
      {pdfUrl && (
        <iframe
          src={pdfUrl + "#toolbar=0&navpanes=0&scrollbar=0"}
          title="Vista previa de cotización"
          className="w-full h-full border-0"
        />
      )}
    </div>
  );
};

const CotizacionSettings = () => {
  const { selectedVenueId, selectedVenue, venueFeatures, cotizacionConfig } = useVenue();
  const isPremium = venueFeatures?.premiumCotizaciones === true;
  const config = cotizacionConfig || {};
  // Inversionista: ve la configuración y la vista previa, sin guardar ni
  // subir logo (P-FRA-13). El fieldset deshabilita todos los controles de
  // un jalón; la vista previa queda fuera y sigue cambiando de modo.
  const { userInfo } = useAuth();
  const soloLectura = esSoloLectura(userInfo);

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewMode, setPreviewMode] = useState("premium");
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    primaryColor: config.primaryColor || "#1b4d2e",
    accentColor: config.accentColor || config.primaryColor || "#1b4d2e",
    secondaryColor: config.secondaryColor || "#f0fdf4",
    nombreCotizacion: config.nombreCotizacion || "",
    logoSize: config.logoSize || 60,
    headerHeight: config.headerHeight || 80,
    showVenueName: config.showVenueName !== false,
    direccion: config.direccion || "",
    telefono: config.telefono || "",
    correo: config.correo || "",
    vigencia: config.vigencia || "30 días",
    leyendaExtra: config.leyendaExtra || "",
  });

  // Keep form in sync if config changes (e.g. first load)
  React.useEffect(() => {
    const c = cotizacionConfig || {};
    setForm({
      primaryColor: c.primaryColor || "#1b4d2e",
      accentColor: c.accentColor || c.primaryColor || "#1b4d2e",
      secondaryColor: c.secondaryColor || "#f0fdf4",
      nombreCotizacion: c.nombreCotizacion || "",
      logoSize: c.logoSize || 60,
      headerHeight: c.headerHeight || 80,
      showVenueName: c.showVenueName !== false,
      direccion: c.direccion || "",
      telefono: c.telefono || "",
      correo: c.correo || "",
      vigencia: c.vigencia || "30 días",
      leyendaExtra: c.leyendaExtra || "",
    });
  }, [cotizacionConfig]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "primaryColor") {
        next.accentColor = value;
        next.secondaryColor = deriveSecondaryColor(value);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!selectedVenueId) return;
    setSaving(true);
    try {
      const configRef = doc(db, "venues", selectedVenueId, "cotizacionConfig", "settings");
      await setDoc(configRef, {
        primaryColor: form.primaryColor,
        accentColor: form.accentColor,
        secondaryColor: form.secondaryColor,
        nombreCotizacion: form.nombreCotizacion,
        logoSize: form.logoSize,
        headerHeight: form.headerHeight,
        showVenueName: form.showVenueName,
        direccion: form.direccion,
        telefono: form.telefono,
        correo: form.correo,
        vigencia: form.vigencia,
        leyendaExtra: form.leyendaExtra,
      }, { merge: true });
      showAlert("Configuración de cotización guardada", "success");
    } catch (err) {
      showAlert(`Error al guardar: ${err.message}`, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = useCallback(
    async (file) => {
      if (!file || !selectedVenueId) return;
      setUploading(true);
      try {
        const path = `venues/${selectedVenueId}/cotizacion-logo-${Date.now()}-${file.name}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(storageRef);

        const configRef = doc(db, "venues", selectedVenueId, "cotizacionConfig", "settings");
        await setDoc(configRef, { logoUrl: downloadUrl }, { merge: true });
        showAlert("Logo actualizado", "success");
      } catch (err) {
        showAlert(`Error al subir logo: ${err.message}`, "error");
      } finally {
        setUploading(false);
      }
    },
    [selectedVenueId]
  );

  const handleRemoveLogo = async () => {
    if (!selectedVenueId) return;
    try {
      const configRef = doc(db, "venues", selectedVenueId, "cotizacionConfig", "settings");
      await setDoc(configRef, { logoUrl: "" }, { merge: true });
      showAlert("Logo eliminado", "success");
    } catch (err) {
      showAlert(`Error: ${err.message}`, "error");
    }
  };

  const logoUrl = config.logoUrl || "";

  // Build the config for the preview based on mode
  const premiumConfig = useMemo(() => ({
    primaryColor: form.primaryColor || "#1b4d2e",
    accentColor: form.accentColor || form.primaryColor || "#1b4d2e",
    secondaryColor: form.secondaryColor || "#f0fdf4",
    nombreCotizacion: form.nombreCotizacion || "",
    logoUrl: logoUrl || "",
    logoSize: form.logoSize || 60,
    headerHeight: form.headerHeight || 80,
    showVenueName: form.showVenueName,
    direccion: form.direccion || "",
    telefono: form.telefono || "",
    correo: form.correo || "",
    vigencia: form.vigencia || "30 días",
    leyendaExtra: form.leyendaExtra || "",
  }), [form, logoUrl]);

  const freeConfig = useMemo(() => ({
    ...FREE_DEFAULTS,
    nombreCotizacion: form.nombreCotizacion || "",
  }), [form.nombreCotizacion]);

  const activePreviewConfig = previewMode === "premium" ? premiumConfig : freeConfig;

  return (
    <div className="mt-8">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-lg font-semibold">Cotizaciones</h2>
        {isPremium ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-0.5 text-xs font-semibold">
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" /></svg>
            Premium activo
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-zinc-100 text-zinc-500 px-2.5 py-0.5 text-xs font-medium">
            Plan gratuito
          </span>
        )}
      </div>

      {!isPremium && !soloLectura && (
        <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
          Configura tus cotizaciones a tu gusto. Al generar el PDF, se usará el diseño por defecto hasta que actives <strong>Premium</strong>. Contacta a Venueboard para activarlo.
        </div>
      )}

      <div className={`grid grid-cols-1 gap-6 ${soloLectura ? "" : "lg:grid-cols-2"}`}>
        {/* LEFT — Settings */}
        <div className="space-y-5">
          <fieldset
            disabled={soloLectura}
            className="min-w-0 rounded-xl border border-zinc-200 bg-white p-5 shadow-sm space-y-5"
          >
            <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-1">
              Personalización
            </div>

            {/* Color de barras */}
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Color de barras (encabezado y pie)<PremiumTag show={!isPremium} />
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.primaryColor}
                  onChange={(e) => handleChange("primaryColor", e.target.value)}
                  className="h-9 w-14 cursor-pointer rounded border border-zinc-300"
                />
                <div
                  className="h-9 flex-1 rounded"
                  style={{ backgroundColor: form.primaryColor }}
                />
              </div>
            </div>

            {/* Accent color */}
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Color de texto y acentos<PremiumTag show={!isPremium} />
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.accentColor}
                  onChange={(e) => handleChange("accentColor", e.target.value)}
                  className="h-9 w-14 cursor-pointer rounded border border-zinc-300"
                />
                <div
                  className="h-9 flex-1 rounded"
                  style={{ backgroundColor: form.accentColor }}
                />
              </div>
              <p className="text-xs text-zinc-400 mt-1">
                Títulos, encabezado de tabla y totales.
              </p>
            </div>

            {/* Secondary color */}
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Color de separadores<PremiumTag show={!isPremium} />
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.secondaryColor}
                  onChange={(e) => handleChange("secondaryColor", e.target.value)}
                  className="h-9 w-14 cursor-pointer rounded border border-zinc-300"
                />
                <div
                  className="h-9 flex-1 rounded"
                  style={{ backgroundColor: form.secondaryColor }}
                />
              </div>
              <p className="text-xs text-zinc-400 mt-1">
                Líneas debajo de las secciones.
              </p>
            </div>

            {/* Venue name */}
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Nombre en cotización
              </label>
              <input
                type="text"
                className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                value={form.nombreCotizacion}
                onChange={(e) => handleChange("nombreCotizacion", e.target.value)}
                placeholder={selectedVenue?.name || "Nombre del venue"}
              />
              <p className="text-xs text-zinc-400 mt-1">
                Si está vacío se usa el nombre del venue.
              </p>
            </div>

            {/* Logo */}
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Logo del venue<PremiumTag show={!isPremium} />
              </label>
              <div className="flex items-center gap-4">
                {logoUrl ? (
                  <div className="relative">
                    <img
                      src={logoUrl}
                      alt="Logo"
                      className="h-16 w-16 object-contain rounded border border-zinc-200"
                    />
                    {!soloLectura && (
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                    >
                      ×
                    </button>
                    )}
                  </div>
                ) : (
                  <div className="h-16 w-16 rounded border-2 border-dashed border-zinc-300 flex items-center justify-center text-zinc-400 text-xs">
                    Logo
                  </div>
                )}
                {!soloLectura && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm font-medium transition hover:underline"
                  style={{ color: ACCENT }}
                  disabled={uploading}
                >
                  {uploading ? "Subiendo..." : "Subir logo"}
                </button>
                )}
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleLogoUpload(file);
                    e.target.value = "";
                  }}
                />
              </div>

              {/* Logo size slider */}
              {logoUrl && (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-zinc-500 mb-1">
                    Tamaño del logo: {form.logoSize}px<PremiumTag show={!isPremium} />
                  </label>
                  <input
                    type="range"
                    min={30}
                    max={120}
                    step={5}
                    value={form.logoSize}
                    onChange={(e) => handleChange("logoSize", Number(e.target.value))}
                    className="w-full accent-[#1b4d2e]"
                  />
                  <div className="flex justify-between text-[10px] text-zinc-400">
                    <span>Pequeño</span>
                    <span>Grande</span>
                  </div>
                </div>
              )}
            </div>

            {/* Header height slider */}
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Altura del encabezado: {form.headerHeight}px<PremiumTag show={!isPremium} />
              </label>
              <input
                type="range"
                min={50}
                max={150}
                step={5}
                value={form.headerHeight}
                onChange={(e) => handleChange("headerHeight", Number(e.target.value))}
                className="w-full accent-[#1b4d2e]"
              />
              <div className="flex justify-between text-[10px] text-zinc-400">
                <span>Compacto</span>
                <span>Amplio</span>
              </div>
            </div>

            {/* Show venue name toggle */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-xs font-medium text-zinc-500">
                  Mostrar nombre en encabezado<PremiumTag show={!isPremium} />
                </label>
                <p className="text-xs text-zinc-400">
                  Desactiva si tu logo ya incluye el nombre.
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleChange("showVenueName", !form.showVenueName)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  form.showVenueName ? "bg-[#1b4d2e]" : "bg-zinc-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    form.showVenueName ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {/* Dirección */}
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Dirección<PremiumTag show={!isPremium} />
              </label>
              <input
                type="text"
                className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                value={form.direccion}
                onChange={(e) => handleChange("direccion", e.target.value)}
                placeholder="Dirección del venue"
              />
            </div>

            {/* Teléfono + Correo */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">
                  Teléfono<PremiumTag show={!isPremium} />
                </label>
                <input
                  type="text"
                  className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                  value={form.telefono}
                  onChange={(e) => handleChange("telefono", e.target.value)}
                  placeholder="Teléfono del venue"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">
                  Correo<PremiumTag show={!isPremium} />
                </label>
                <input
                  type="email"
                  className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                  value={form.correo}
                  onChange={(e) => handleChange("correo", e.target.value)}
                  placeholder="correo@venue.com"
                />
              </div>
            </div>

            {/* Vigencia */}
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Vigencia de la cotización<PremiumTag show={!isPremium} />
              </label>
              <input
                type="text"
                className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                value={form.vigencia}
                onChange={(e) => handleChange("vigencia", e.target.value)}
                placeholder="30 días"
              />
            </div>

            {/* Leyenda extra */}
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Leyenda / Términos adicionales<PremiumTag show={!isPremium} />
              </label>
              <textarea
                className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e] min-h-[80px]"
                value={form.leyendaExtra}
                onChange={(e) => handleChange("leyendaExtra", e.target.value)}
                placeholder="Términos y condiciones opcionales..."
                rows={3}
              />
            </div>

            {!soloLectura && (
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center justify-center rounded-lg px-5 py-2 text-sm font-semibold text-white shadow-sm transition disabled:opacity-50"
                style={{ backgroundColor: ACCENT }}
              >
                {saving ? "Guardando..." : "Guardar configuración"}
              </button>
            )}
          </fieldset>
        </div>

        {/* RIGHT — Preview with toggle. Solo lectura no ve la vista previa:
            es un PDF que se puede guardar desde el navegador (P-FRA-13). */}
        {!soloLectura && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
              Vista previa
            </div>
            <div className="flex rounded-lg border border-zinc-300 overflow-hidden text-xs">
              <button
                type="button"
                onClick={() => setPreviewMode("premium")}
                className={`px-3 py-1.5 font-medium transition-colors ${
                  previewMode === "premium"
                    ? "bg-[#1b4d2e] text-white"
                    : "bg-white text-zinc-500 hover:bg-zinc-50"
                }`}
              >
                Con Premium
              </button>
              <button
                type="button"
                onClick={() => setPreviewMode("free")}
                className={`px-3 py-1.5 font-medium transition-colors ${
                  previewMode === "free"
                    ? "bg-[#1b4d2e] text-white"
                    : "bg-white text-zinc-500 hover:bg-zinc-50"
                }`}
              >
                Sin Premium
              </button>
            </div>
          </div>

          {/* Upgrade banner when viewing free mode and not premium */}
          {previewMode === "free" && !isPremium && (
            <div className="mb-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
              Así se ve tu cotización actualmente. Activa Premium para usar tu diseño personalizado.
            </div>
          )}

          {/* Already premium viewing free */}
          {previewMode === "free" && isPremium && (
            <div className="mb-2 rounded-lg bg-zinc-50 border border-zinc-200 px-3 py-2 text-xs text-zinc-500">
              Así se vería sin Premium. Tu cuenta ya tiene Premium activo.
            </div>
          )}

          {/* Viewing premium but not premium */}
          {previewMode === "premium" && !isPremium && (
            <div className="mb-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800">
              Así se verían tus cotizaciones con Premium. Contacta a Venueboard para activarlo.
            </div>
          )}

          <CotizacionPreview
            config={activePreviewConfig}
            isPremium={previewMode === "premium"}
            venueName={selectedVenue?.name || "Mi Venue"}
            previewMode={previewMode}
          />
        </div>
        )}
      </div>
    </div>
  );
};

export default CotizacionSettings;
